import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { FaEdit, FaTrash, FaDownload, FaSpinner, FaPlus, FaFilter } from 'react-icons/fa';
import axiosInstance from '../config/axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import ReceiptFilters from './ReceiptFilters';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TransportReceipt = () => {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalAmount, setTotalAmount] = useState(0);
    const [balancePayment, setBalancePayment] = useState(0);
    const [activeFilters, setActiveFilters] = useState({});
    const [editingReceipt, setEditingReceipt] = useState(null);
    const [vehicleDetails, setVehicleDetails] = useState(null);
    const [verifyingVehicle, setVerifyingVehicle] = useState(false);
    const { register, handleSubmit, reset, setValue, formState: { errors }, watch } = useForm();
    const { user } = useSelector((state) => state.user);

    const fetchReceipts = async (page = 1, filters = {}) => {
        try {
            setLoading(true);
            // Format dates if present
            const formattedFilters = {
                ...filters,
                page,
                fromDate: filters.fromDate ? new Date(filters.fromDate).toISOString() : undefined,
                toDate: filters.toDate ? new Date(filters.toDate).toISOString() : undefined
            };

            // Remove undefined values
            const cleanFilters = Object.entries(formattedFilters).reduce((acc, [key, value]) => {
                if (value !== undefined && value !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const queryParams = new URLSearchParams(cleanFilters);
            console.log('Filters:', cleanFilters); // For debugging

            const response = await axiosInstance.get(`/api/transport-receipts/list?${queryParams}`);
            setReceipts(response.data.data);
            setTotalPages(response.data.pagination.pages);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching receipts:', error);
            Swal.fire(
                'Error!',
                'Failed to fetch receipts',
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReceipts(1, activeFilters);
    }, [activeFilters]);

    useEffect(() => {
        if (editingReceipt) {
            // Set form values for editing
            setValue('fromLocation', editingReceipt.fromLocation);
            setValue('toLocation', editingReceipt.toLocation);
            setValue('consignorName', editingReceipt.consignorDetails.name);
            setValue('consignorAddress', editingReceipt.consignorDetails.address);
            setValue('consignorContact', editingReceipt.consignorDetails.contact);
            setValue('consignorGstin', editingReceipt.consignorDetails.gstin);
            setValue('consigneeName', editingReceipt.consigneeDetails.name);
            setValue('consigneeAddress', editingReceipt.consigneeDetails.address);
            setValue('consigneeContact', editingReceipt.consigneeDetails.contact);
            setValue('consigneeGstin', editingReceipt.consigneeDetails.gstin);
            setValue('goodsDescription', editingReceipt.goodsDescription);
            setValue('weight', editingReceipt.weight);
            setValue('rate', editingReceipt.rate);
            setValue('vehicleNumber', editingReceipt.vehicleNumber);
            setValue('driverName', editingReceipt.driverDetails?.name);
            setValue('driverContact', editingReceipt.driverDetails?.contact);
            setValue('driverLicense', editingReceipt.driverDetails?.licenseNumber);
            setValue('paymentStatus', editingReceipt.paymentStatus);
            setValue('remarks', editingReceipt.remarks);
            setValue('chassisNumber', editingReceipt.chassisNumber);
            setValue('engineNumber', editingReceipt.engineNumber);
            setValue('advancePayment', editingReceipt.advancePayment);
            setValue('referenceName', editingReceipt.referenceName);
            setValue('referenceContact', editingReceipt.referenceContact);
            setValue('ownerName', editingReceipt.ownerName);
            setValue('ownerNumber', editingReceipt.ownerNumber);
        }
    }, [editingReceipt, setValue]);

    const handleApplyFilters = (filters) => {
        // Clean up filters before setting
        const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {});

        setActiveFilters(cleanFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setActiveFilters({});
        setCurrentPage(1);
    };

    const handleEdit = (receipt) => {
        Swal.fire({
            title: 'Edit Receipt',
            text: `Do you want to edit receipt ${receipt.receiptNumber}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, edit it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setEditingReceipt(receipt);
                setShowForm(true);
            }
        });
    };

    const handleDelete = async (receiptId) => {
        const result = await Swal.fire({
            title: 'Delete Receipt',
            text: 'Are you sure you want to delete this receipt? This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, keep it'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                await axiosInstance.delete(`/api/transport-receipts/${receiptId}`);

                Swal.fire(
                    'Deleted!',
                    'Receipt has been deleted successfully.',
                    'success'
                );

                fetchReceipts(currentPage, activeFilters);
            } catch (error) {
                console.error('Error deleting receipt:', error);
                Swal.fire(
                    'Error!',
                    error.response?.data?.message || 'Failed to delete receipt',
                    'error'
                );
            } finally {
                setLoading(false);
            }
        }
    };

    const downloadPDF = async (id) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/api/transport-receipts/${id}/pdf`);
            const data = response.data.data;

            const doc = new jsPDF({
                orientation: 'p',
                unit: 'mm',
                format: 'a4'
            });

            // Get page dimensions
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // Add a stylish gradient background
            doc.setFillColor(240, 248, 255); // Light blue shade
            doc.rect(0, 0, pageWidth, pageHeight, 'F');

            // HEADER DESIGN (Modern Look)
            doc.setFillColor(33, 150, 243); // Blue background
            doc.rect(0, 0, pageWidth, 30, 'F'); // Increased height to fit all header text

            // "All Disputes" Text (Fixed Alignment)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(255, 255, 255);
            doc.text('All Disputes under Allahabad Court', pageWidth / 2, 8, { align: "center" });

            // Company Name
            doc.setFontSize(20);
            doc.text('KANHAIYA LOGISTIC', pageWidth / 2, 15, { align: "center" });

            // Regular Services Info
            doc.setFontSize(10);
            doc.text('Regular Services - Maharashtra, MP, Odisha, Bihar, etc.', pageWidth / 2, 22, { align: "center" });

            // Address (Newly Added)
            doc.setFontSize(10);
            doc.text('(In front of Kesarwani Store) Soraon, Prayagraj 212502', pageWidth / 2, 28, { align: "center" });

            // Sub-header with Contact Info
            doc.setFontSize(12);
            doc.setTextColor(100, 100, 100);
            doc.text('Mo: 9651538180, 9935382689', pageWidth / 2, 35, { align: "center" });

            // Separator Line
            doc.setDrawColor(33, 150, 243);
            doc.setLineWidth(0.75);
            doc.line(10, 40, pageWidth - 10, 40);

            // RECEIPT DETAILS (Stylish & Clean)
            doc.setFontSize(14);
            doc.setTextColor(33, 150, 243);
            doc.setFont('helvetica', 'bold');
            doc.text(`Receipt No: ${data.receiptNumber || ''}`, 15, 45);
            doc.text(`Date: ${new Date(data.date).toLocaleDateString()}`, pageWidth - 50, 45);

            // LEFT SIDE DETAILS (From Location, Sender, Driver, Vehicle)
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(33, 150, 243);
            doc.text('From Location:', 15, 55);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            doc.text(`${data.fromLocation || 'N/A'}`, 15, 61);

            doc.setFont('helvetica', 'bold');
            doc.text('Sender Details:', 15, 71);
            doc.setFont('helvetica', 'normal');
            doc.text(`Name: ${data.consignorDetails.name || ''}`, 15, 77);
            doc.text(`Address: ${data.consignorDetails.address || ''}`, 15, 83);
            doc.text(`Contact: ${data.consignorDetails.contact || ''}`, 15, 89);

            doc.setFont('helvetica', 'bold');
            doc.text('Driver Details:', 15, 99);
            doc.setFont('helvetica', 'normal');
            doc.text(`Name: ${data.driverDetails.name || ''}`, 15, 105);
            doc.text(`Contact: ${data.driverDetails.contact || ''}`, 15, 111);

            doc.setFont('helvetica', 'bold');
            doc.text('Vehicle No:', 15, 121);
            doc.setFont('helvetica', 'normal');
            doc.text(`${data.vehicleNumber || ''}`, 15, 127);

            // RIGHT SIDE DETAILS (To Location, Receiver, Owner, Reference)
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(33, 150, 243);
            doc.text('To Location:', pageWidth / 2 + 15, 55);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            doc.text(`${data.toLocation || 'N/A'}`, pageWidth / 2 + 15, 61);

            doc.setFont('helvetica', 'bold');
            doc.text('Receiver Details:', pageWidth / 2 + 15, 71);
            doc.setFont('helvetica', 'normal');
            doc.text(`Name: ${data.consigneeDetails.name || ''}`, pageWidth / 2 + 15, 77);
            doc.text(`Address: ${data.consigneeDetails.address || ''}`, pageWidth / 2 + 15, 83);
            doc.text(`Contact: ${data.consigneeDetails.contact || ''}`, pageWidth / 2 + 15, 89);

            doc.setFont('helvetica', 'bold');
            doc.text('Owner Details:', pageWidth / 2 + 15, 99);
            doc.setFont('helvetica', 'normal');
            doc.text(`Name: ${data.ownerName || ''}`, pageWidth / 2 + 15, 105);
            doc.text(`Contact: ${data.ownerNumber || ''}`, pageWidth / 2 + 15, 111);

            doc.setFont('helvetica', 'bold');
            doc.text('Reference Details:', pageWidth / 2 + 15, 121);
            doc.setFont('helvetica', 'normal');
            doc.text(`Name: ${data.referenceName || ''}`, pageWidth / 2 + 15, 127);
            doc.text(`Contact: ${data.referenceContact || ''}`, pageWidth / 2 + 15, 133);

            // GOODS TABLE (Improved Styling)
            autoTable(doc, {
                startY: 145, // Adjusted startY to accommodate the new layout
                head: [['S.No', 'Goods Description', 'Weight (Qtl)', 'Full Payment', 'Advance Payment', 'Balance Payment']],
                body: [[
                    '1',
                    data.goodsDescription || '',
                    `${data.weight || ''}`,
                    `${data.totalAmount.toLocaleString() || ''}`,
                    `${data.advancePayment.toLocaleString() || ''}`,
                    `${(data.totalAmount - data.advancePayment).toLocaleString() || ''}`
                ]],
                styles: {
                    fontSize: 10,
                    halign: 'center',
                    cellPadding: 6,
                    lineColor: [33, 150, 243],
                    lineWidth: 0.3
                },
                headStyles: {
                    fillColor: [33, 150, 243],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold'
                },
                alternateRowStyles: { fillColor: [245, 245, 245] }
            });

            // NOTICE SECTION (Highlighted Text)
            doc.setFontSize(10);
            doc.setTextColor(255, 0, 0);
            doc.text('Notice:', 15, doc.autoTable.previous.finalY + 10);
            doc.setTextColor(0, 0, 0);
            doc.text('The driver or owner himself will be responsible for counting the goods and getting the goods wet.', 15, doc.autoTable.previous.finalY + 15);

            // SIGNATURE SECTION (Proper Alignment)
            const signatureY = doc.autoTable.previous.finalY + 30;
            const lineSpacing = 25; // Increase spacing to provide at least 1 inch (25.4mm) of space

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            doc.text('Signing Authorized:', 15, signatureY);
            doc.text('Consigner:', 15, signatureY + lineSpacing);
            doc.line(15, signatureY + lineSpacing + 5, 60, signatureY + lineSpacing + 5);

            doc.text('Truck Owner:', 70, signatureY + lineSpacing);
            doc.line(70, signatureY + lineSpacing + 5, 115, signatureY + lineSpacing + 5);

            doc.text('Driver:', 120, signatureY + lineSpacing);
            doc.line(120, signatureY + lineSpacing + 5, 165, signatureY + lineSpacing + 5);

            doc.text('Booking Clerk:', 170, signatureY + lineSpacing);
            doc.line(170, signatureY + lineSpacing + 5, 200, signatureY + lineSpacing + 5);

            // FOOTER (Bolder + Modern Look)
            doc.setFontSize(14);
            doc.setTextColor(33, 150, 243);
            doc.text('Thank you for choosing Kanhaiya Logistic!', pageWidth / 2, 280, { align: "center" });

            // ✅ Add a new page for Important Rules and Footer
            doc.addPage();

            // Set pink background for the second page
            doc.setFillColor(240, 248, 255); // Light blue shade
            doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), 'F'); // Fill the entire page

            // ✅ Residential Rules Notice (Important Notice Section)
            doc.setFontSize(16); // Larger font size for the notice
            doc.setTextColor(255, 0, 0); // Red color for importance
            doc.setFont('helvetica', 'bold');
            doc.text('Important Notice:', 15, 20); // Positioned at the top of the second page

            doc.setFontSize(12); // Smaller font size for the rules
            doc.setTextColor(0, 0, 0); // Black color for the rules
            doc.setFont('helvetica', 'normal');
            const rules = [
                '1. The agency will not be responsible for the goods getting wet, lack of liquids or breakage. All this will be the responsibility of the owner.',
                '2. Fresh fruits are booked at the responsibility of the customer.',
                '3. The agency will try to send full or half of the consignment as quickly as possible.',
                "4. Delivery of goods will be made at the agency's office unless otherwise specified in writing.",
                '5. Goods will be delivered after full payment of agency amount',
                '6. Delivery of goods will have to be taken within 3 days of delivery otherwise 25 paise per quintal damage will have to be paid daily.',
                '7. If any claim is regarding the goods then it should be filed within 15 days otherwise after that it will be considered useless. Every type of matter will be decided in Allahabad High Court.',
                '8. The agency will not be responsible for road accidents and toll disputes.',
                '9. If any difference is found between the receipt and the balance of the goods, then 25 percent more will have to be paid on the basis of the full balance.',
                '10. If the goods do not arrive within a month from the date of booking, the sending agency will have to pay the amount through the gravel register. If this is not done within the stipulated time, the agency will not be responsible.',
                "11. If the goods are not delivered within a month, the agency will have the right to auction them publicly. If there is still a shortfall in the agency's rent, the court can recover it from the sender.",
                '12. If any dispute arises regarding sending the goods against the control rules or regulations, the merchant himself will be responsible and the travel expenses will be reasonable.'
            ];

            let y = 30; // Starting Y position for the rules
            rules.forEach(rule => {
                doc.text(rule, 20, y, { maxWidth: 170 }); // Indent the rules and set max width for better readability
                y += 17; // Adjust spacing between rules
            });

            // ✅ Footer (Spaced and styled)
            doc.setFontSize(14); // Larger font size for the footer
            doc.setTextColor(100, 100, 100); // Grey color
            doc.text('Thank you for choosing Kanhaiya Logistic!', 105, 280, { align: "center" });

            // ✅ Save the PDF
            doc.save(`receipt-${data.vehicleNumber}.pdf`);

            Swal.fire({
                title: 'Success!',
                text: 'PDF downloaded successfully',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.fire('Error!', 'Failed to download PDF', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchVehicle = async (vehicleNumber) => {

        const responseExistTrucks = await axiosInstance.get(`/api/trucks/get/${vehicleNumber}`);
        return responseExistTrucks?.data;


        // try {
        //     const response = await fetch(
        //         "https://api.invincibleocean.com/invincible/vehicleRegistrationV10",
        //         {
        //             method: "POST",
        //             headers: {
        //                 "Content-Type": "application/json",
        //                 clientId: clientId,
        //                 secretKey: secretKey,
        //             },
        //             body: JSON.stringify({ vehicleNumber }),
        //         }
        //     );

        //     if (!response.ok) {
        //         throw new Error(`HTTP error! Status: ${response.status}`);
        //     }

        //     const data = await response.json();
        //     console.log("Vehicle Data:", data);
        //     return data;
        // } catch (error) {
        //     console.error("Error fetching vehicle data:", error);
        //     return null;
        // }
    };

    const verifyVehicle = async (vehicleNo) => {
        if (!vehicleNo || vehicleNo.length < 8) return;

        try {
            setVerifyingVehicle(true);
            const response = await fetchVehicle(vehicleNo)
            console.log(response[0].bulkData)


            if (response.length !== 0) {
                setVehicleDetails(response[0].bulkData);
                // Auto-fill owner name as driver name if available
                if (response[0].bulkData.owner_name) {
                    setValue('ownerName', response[0].bulkData.owner_name);
                }

                // Format dates for better display
                const formatDate = (dateStr) => dateStr !== '--' ? dateStr : 'Not Available';

                // Auto-fill chassis number and engine number
                if (response[0].bulkData.vehicle_chasi_number) {
                    setValue('chassisNumber', response[0].bulkData.vehicle_chasi_number);
                }
                if (response[0].bulkData.vehicle_engine_number) {
                    setValue('engineNumber', response[0].bulkData.vehicle_engine_number);
                }

                Swal.fire({
                    title: 'Vehicle Verified!',
                    html: `
                        <div class="text-left space-y-3">
                            <div class="font-semibold text-lg mb-3 text-blue-600 border-b pb-2">Vehicle Registration Details</div>
                            <p><strong>Registration No:</strong> ${response[0].bulkData.rc_number}</p>
                            <p><strong>Registered At:</strong> ${response[0].bulkData.registered_at}</p>
                            <p><strong>Registration Date:</strong> ${formatDate(response[0].bulkData.registration_date)}</p>
                
                            <div class="font-semibold text-lg mt-4 mb-3 text-blue-600 border-b pb-2">Vehicle Information</div>
                            <p><strong>Make & Model:</strong> ${response[0].bulkData.maker_description} ${response[0].bulkData.maker_model}</p>
                            <p><strong>Vehicle Category:</strong> ${response[0].bulkData.vehicle_category}</p>
                            <p><strong>Fuel Type:</strong> ${response[0].bulkData.fuel_type}</p>
                            <p><strong>Manufacturing Date:</strong> ${response[0].bulkData.manufacturing_date_formatted}</p>
                            <p><strong>Chassis No:</strong> ${response[0].bulkData.vehicle_chasi_number}</p>
                            <p><strong>Engine No:</strong> ${response[0].bulkData.vehicle_engine_number}</p>
                
                            <div class="font-semibold text-lg mt-4 mb-3 text-blue-600 border-b pb-2">Owner Details</div>
                            <p><strong>Owner Name:</strong> ${response[0].bulkData.owner_name}</p>
                            <p><strong>Father's Name:</strong> ${response[0].bulkData.father_name}</p>
                            <p><strong>Address:</strong> ${response[0].bulkData.present_address}</p>
                
                            <div class="font-semibold text-lg mt-4 mb-3 text-blue-600 border-b pb-2">Compliance Status</div>
                            <p><strong>Vehicle Status:</strong> <span class="text-${response[0].bulkData.rc_status === 'Y' ? 'green' : 'red'}-600 font-bold">${response[0].bulkData.rc_status === 'Y' ? 'Active' : 'Inactive'}</span></p>
                            <p><strong>Insurance:</strong> ${response[0].bulkData.insurance_company}</p>
                            <p><strong>Insurance Valid Till:</strong> ${formatDate(response[0].bulkData.insurance_upto)}</p>
                            <p><strong>Fitness Valid Till:</strong> ${formatDate(response[0].bulkData.fit_up_to)}</p>
                            <p><strong>PUC Valid Till:</strong> ${formatDate(response[0].bulkData.pucc_upto)}</p>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonText: 'OK',
                    width: '800px'
                });

            } else {
                Swal.fire({
                    title: 'Verification Failed',
                    text: "Verify vehicle details or confirm if this vehicle is added in the 'Add Truck' option.",
                    icon: 'error'
                });
            }
        } catch (error) {
            console.error('Error verifying vehicle:', error);
            Swal.fire({
                title: 'Error',
                text: "Verify vehicle details or confirm if this vehicle is added in the 'Add Truck' option.",
                icon: 'error'
            });
        } finally {
            setVerifyingVehicle(false);
        }
    };

    // Add validation patterns
    const chassisPattern = /^[A-HJ-NPR-Z0-9]{17}$/i;
    const enginePattern = /^[A-Z0-9]{6,14}$/i;

    // Add validation messages
    const getChassisError = (value) => {
        if (!value) return '';
        if (!chassisPattern.test(value)) {
            return 'Chassis number should be 17 characters long and contain only letters (except I, O, Q) and numbers';
        }
        return '';
    };

    const getEngineError = (value) => {
        if (!value) return '';
        if (!enginePattern.test(value)) {
            return 'Engine number should be 6-14 characters long and contain only letters and numbers';
        }
        return '';
    };

    // Add debounce to avoid too many API calls
    const debouncedVerifyVehicle = useCallback(
        debounce((value) => verifyVehicle(value), 1000),
        []
    );

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name === 'vehicleNumber') {
                debouncedVerifyVehicle(value.vehicleNumber);
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, debouncedVerifyVehicle]);

    const onSubmit = async (data) => {
        try {
            // Check if user is logged in and has an ID
            if (!user || !user._id) {
                Swal.fire({
                    title: 'Error!',
                    text: 'You must be logged in to create or edit receipts',
                    icon: 'error'
                });
                return;
            }

            // Check if auth token exists
            const token = localStorage.getItem('tokenData');
            if (!token) {
                Swal.fire({
                    title: 'Authentication Error',
                    text: 'Your session has expired. Please log in again.',
                    icon: 'error'
                }).then(() => {
                    // Redirect to login page
                    window.location.href = '/login';
                });
                return;
            }

            setLoading(true);
            const totalAmount = parseFloat(data.weight) * parseFloat(data.rate);

            const formData = {
                date: data.date || new Date(),
                fromLocation: data.fromLocation,
                toLocation: data.toLocation,
                consignorDetails: {
                    name: data.consignorName,
                    address: data.consignorAddress,
                    contact: data.consignorContact,
                    gstin: data.consignorGstin
                },
                consigneeDetails: {
                    name: data.consigneeName,
                    address: data.consigneeAddress,
                    contact: data.consigneeContact,
                    gstin: data.consigneeGstin
                },
                goodsDescription: data.goodsDescription,
                weight: parseFloat(data.weight),
                rate: parseFloat(data.rate),
                totalAmount,
                vehicleNumber: data.vehicleNumber,
                chassisNumber: data.chassisNumber,
                engineNumber: data.engineNumber,
                driverDetails: {
                    name: data.driverName,
                    contact: data.driverContact,
                    licenseNumber: data.driverLicense
                },
                paymentStatus: data.paymentStatus || 'PENDING',
                remarks: data.remarks,
                advancePayment: data.advancePayment,
                referenceName: data.referenceName,
                referenceContact: data.referenceContact,
                ownerName: data.ownerName,
                ownerNumber: data.ownerNumber,
                createdBy: user._id
            };

            if (editingReceipt) {
                const response = await axiosInstance.put(`/api/transport-receipts/${editingReceipt._id}`, formData);
                Swal.fire({
                    title: 'Success!',
                    text: `Receipt ${response.data.data.receiptNumber} updated successfully`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                const response = await axiosInstance.post('/api/transport-receipts/create', formData);
                Swal.fire({
                    title: 'Success!',
                    html: `Receipt created successfully<br>Receipt Number: <strong>${response.data.data.receiptNumber}</strong>`,
                    icon: 'success',
                    timer: 3000,
                    showConfirmButton: true,
                    confirmButtonText: 'OK'
                });
            }

            setShowForm(false);
            setEditingReceipt(null);
            reset();
            fetchReceipts(currentPage, activeFilters);
        } catch (error) {
            console.error('Error saving receipt:', error);
            let errorMessage = 'Failed to save receipt';

            // Handle specific error cases
            if (error.response?.status === 401) {
                errorMessage = 'Your session has expired. Please log in again.';
                localStorage.removeItem('token');
                setTimeout(() => {
                    window.location.href = '/login';
                }, 2000);
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            Swal.fire({
                title: 'Error!',
                text: errorMessage,
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseForm = () => {
        if (editingReceipt || Object.keys(errors).length > 0) {
            Swal.fire({
                title: 'Are you sure?',
                text: 'Any unsaved changes will be lost!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, close it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    setShowForm(false);
                    setEditingReceipt(null);
                    reset();
                }
            });
        } else {
            setShowForm(false);
            setEditingReceipt(null);
            reset();
        }
    };

    const handleWeightChange = (e) => {
        const weightValue = parseFloat(e.target.value);
        const rateValue = parseFloat(watch('rate')); // Get current rate
        const calculatedTotal = weightValue * (isNaN(rateValue) ? 0 : rateValue);
        setTotalAmount(calculatedTotal);
    };

    const handleRateChange = (e) => {
        const rateValue = parseFloat(e.target.value);
        const weightValue = parseFloat(watch('weight')); // Get current weight
        const calculatedTotal = (isNaN(weightValue) ? 0 : weightValue) * rateValue;
        setTotalAmount(calculatedTotal);
    };

    const handleAdvancePaymentChange = (e) => {
        const advanceValue = parseFloat(e.target.value);
        setBalancePayment(totalAmount - (isNaN(advanceValue) ? 0 : advanceValue));
    };

    const renderReceiptTable = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            );
        }

        if (!receipts.length) {
            return (
                <div className="text-center p-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No receipts found</p>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg overflow-hidden">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Receipt No.</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Vehicle Details</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Driver</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Owner</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Reference</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">From</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">To</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Consignor</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Consignee</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Amount (₹)</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Payment</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Advance Payment</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Balance Payment</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {receipts.map((receipt) => (
                            <tr
                                key={receipt._id}
                                className="hover:bg-gray-50 transition-colors duration-200"
                            >
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                    {receipt.receiptNumber}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                    {new Date(receipt.date).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="text-gray-900 font-medium">{receipt.vehicleNumber}</div>
                                    {(receipt.chassisNumber || receipt.engineNumber) && (
                                        <div className="text-xs text-gray-500 mt-1">
                                            {receipt.chassisNumber && (
                                                <div title="Chassis Number">
                                                    CH: {receipt.chassisNumber}
                                                </div>
                                            )}
                                            {receipt.engineNumber && (
                                                <div title="Engine Number">
                                                    EN: {receipt.engineNumber}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="text-gray-900">{receipt.driverDetails?.name || '-'}</div>
                                    {receipt.driverDetails?.contact && (
                                        <div className="text-gray-500 text-xs">
                                            {receipt.driverDetails.contact}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="text-gray-900">{receipt.ownerName || '-'}</div>
                                    <div className="text-gray-900">{receipt.ownerNumber}</div>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="text-gray-900">{receipt.referenceName || '-'}</div>
                                    <div className="text-gray-900">{receipt.referenceContact}</div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                    {receipt.fromLocation}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                    {receipt.toLocation}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="text-gray-900">{receipt.consignorDetails.name}</div>
                                    <div className="text-gray-500 text-xs">
                                        {receipt.consignorDetails.contact}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="text-gray-900">{receipt.consigneeDetails.name}</div>
                                    <div className="text-gray-500 text-xs">
                                        {receipt.consigneeDetails.contact}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                    ₹{receipt.totalAmount.toLocaleString('en-IN')}
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                        ${receipt.status === 'ACTIVE' ? 'bg-yellow-100 text-yellow-800' : ''}
                                        ${receipt.status === 'DELIVERED' ? 'bg-green-100 text-green-800' : ''}
                                        ${receipt.status === 'CANCELLED' ? 'bg-red-100 text-red-800' : ''}
                                    `}>
                                        {receipt.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                        ${receipt.paymentStatus === 'PAID' ? 'bg-green-100 text-green-800' : ''}
                                        ${receipt.paymentStatus === 'PENDING' ? 'bg-red-100 text-red-800' : ''}
                                        ${receipt.paymentStatus === 'PARTIAL' ? 'bg-yellow-100 text-yellow-800' : ''}
                                    `}>
                                        {receipt.paymentStatus}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{receipt.advancePayment}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{receipt.totalAmount - receipt.advancePayment}</td>
                                <td className="px-4 py-3 text-sm text-right space-x-2">
                                    <div className="flex justify-center space-x-2">
                                        <button
                                            onClick={() => handleEdit(receipt)}
                                            className="text-blue-600 hover:text-blue-800"
                                            title="Edit Receipt"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            onClick={() => downloadPDF(receipt._id)}
                                            className="text-green-600 hover:text-green-800"
                                            title="Download PDF"
                                        >
                                            <FaDownload />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(receipt._id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Delete Receipt"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Transport Receipts</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                        <FaFilter /> Filters
                    </button>
                    <button
                        onClick={() => setShowForm(true)}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                        <FaPlus /> New Receipt
                    </button>
                </div>
            </div>

            {showFilters && (
                <ReceiptFilters
                    onApplyFilters={handleApplyFilters}
                    onResetFilters={handleResetFilters}
                />
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-10 z-50 overflow-y-auto">
                    <div className="bg-white p-6 rounded-lg w-full max-w-4xl mx-4 mb-10 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-800">{editingReceipt ? 'Edit Receipt' : 'Create New Receipt'}</h2>
                            <button
                                onClick={handleCloseForm}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            {/* Basic Information */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4 text-gray-700">Basic Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {editingReceipt && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Number</label>
                                            <div className="w-full px-3 py-2 bg-gray-100 rounded-md text-gray-700">
                                                {editingReceipt.receiptNumber}
                                            </div>
                                        </div>
                                    )}

                                    {/* Date Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date*</label>
                                        <input
                                            type="date"
                                            {...register("date", { required: "Date is required" })}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date.message}</p>}
                                    </div>

                                    {/* Vehicle Information Section */}
                                    <div className="bg-gray-50 p-5 rounded-lg shadow-md col-span-1 md:col-span-2 lg:col-span-3">
                                        <h3 className="text-lg font-semibold mb-4 text-gray-700">Vehicle Information</h3>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Vehicle Number */}
                                            <div className="relative">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Vehicle Number*
                                                    {verifyingVehicle && <span className="ml-2 text-blue-500 text-xs">Verifying...</span>}
                                                </label>
                                                <div className="mt-1 relative">
                                                    <input
                                                        type="text"
                                                        {...register("vehicleNumber", {
                                                            required: "Vehicle number is required",
                                                            pattern: {
                                                                value: /^[A-Z]{2}[0-9]{1,2}[A-Z]{1,2}[0-9]{4}$/,
                                                                message: "Please enter a valid vehicle number (e.g., UP32JR1805)",
                                                            },
                                                        })}
                                                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md px-3 py-2"
                                                    />
                                                    {verifyingVehicle && (
                                                        <div className="absolute right-2 top-2">
                                                            <FaSpinner className="animate-spin text-blue-500" />
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.vehicleNumber && <p className="text-red-500 text-xs mt-1">{errors.vehicleNumber.message}</p>}
                                            </div>

                                            {/* Chassis Number */}
                                            <div className="relative group">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Chassis Number <span className="ml-1 text-gray-400">(optional)</span>
                                                    <span className="ml-1 text-gray-400 cursor-help">ⓘ</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register("chassisNumber")}
                                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md uppercase px-3 py-2"
                                                    placeholder="e.g., 1HGCM82633A123456"
                                                />
                                                <div className="absolute hidden group-hover:block z-10 w-72 p-2 mt-1 text-sm text-gray-500 bg-white border rounded shadow-lg">
                                                    The chassis number (VIN) is a 17-character unique identifier for your vehicle.
                                                    It can be found on your RC book or physically on your vehicle.
                                                </div>
                                                {errors.chassisNumber && <p className="text-red-500 text-xs mt-1">{errors.chassisNumber.message}</p>}
                                            </div>

                                            {/* Engine Number */}
                                            <div className="relative group">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Engine Number <span className="ml-1 text-gray-400">(optional)</span>
                                                    <span className="ml-1 text-gray-400 cursor-help">ⓘ</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register("engineNumber")}
                                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md uppercase px-3 py-2"
                                                    placeholder="e.g., ABCD123456789"
                                                />
                                                <div className="absolute hidden group-hover:block z-10 w-72 p-2 mt-1 text-sm text-gray-500 bg-white border rounded shadow-lg">
                                                    The engine number is a unique identifier for your vehicle's engine.
                                                    It can be found on your RC book or physically on the engine block.
                                                </div>
                                                {errors.engineNumber && <p className="text-red-500 text-xs mt-1">{errors.engineNumber.message}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name*</label>
                                                <input
                                                    type="text"
                                                    {...register("ownerName")}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Enter owner name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Number*</label>
                                                <input
                                                    type="text"
                                                    {...register("ownerNumber")}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Enter owner number"
                                                />
                                            </div>

                                        </div>
                                    </div>
                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">From Location*</label>
                                        <input
                                            {...register('fromLocation', { required: 'From location is required' })}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter origin location"
                                        />
                                        {errors.fromLocation && (
                                            <p className="text-red-500 text-xs mt-1">{errors.fromLocation.message}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">To Location*</label>
                                        <input
                                            {...register('toLocation', { required: 'To location is required' })}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter destination location"
                                        />
                                        {errors.toLocation && (
                                            <p className="text-red-500 text-xs mt-1">{errors.toLocation.message}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Consignor Details */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4 text-gray-700">Consignor Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Name*</label>
                                        <input
                                            {...register('consignorName', { required: 'Consignor name is required' })}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter consignor name"
                                        />
                                        {errors.consignorName && (
                                            <p className="text-red-500 text-xs mt-1">{errors.consignorName.message}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                                        <input
                                            {...register('consignorContact')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter contact number"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                        <textarea
                                            {...register('consignorAddress')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            rows="2"
                                            placeholder="Enter complete address"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Consignee Details */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4 text-gray-700">Consignee Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Name*</label>
                                        <input
                                            {...register('consigneeName', { required: 'Consignee name is required' })}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter consignee name"
                                        />
                                        {errors.consigneeName && (
                                            <p className="text-red-500 text-xs mt-1">{errors.consigneeName.message}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                                        <input
                                            {...register('consigneeContact')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter contact number"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                        <textarea
                                            {...register('consigneeAddress')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            rows="2"
                                            placeholder="Enter complete address"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Goods Details */}
                            <div className="bg-white shadow-lg rounded-xl p-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-8">Goods Details</h3>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description*</label>
                                        <input
                                            {...register('goodsDescription', { required: 'Goods description is required' })}
                                            className="w-full border border-gray-300 rounded-xl px-4 py-2 shadow-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="Enter goods description"
                                        />
                                        {errors.goodsDescription && (
                                            <p className="text-red-500 text-xs mt-1">{errors.goodsDescription.message}</p>
                                        )}
                                    </div>

                                    {/* Weight */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Weight (quintal)*</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            {...register('weight', {
                                                required: 'Weight is required',
                                                min: { value: 0.01, message: 'Weight must be greater than 0' }
                                            })}
                                            onChange={handleWeightChange}
                                            className="w-full border border-gray-300 rounded-xl px-4 py-2 shadow-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="Enter weight"
                                        />
                                        {errors.weight && <p className="text-red-500 text-xs mt-1">{errors.weight.message}</p>}
                                    </div>

                                    {/* Rate */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Rate (per quintal)*</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            {...register('rate', {
                                                required: 'Rate is required',
                                                min: { value: 0.01, message: 'Rate must be greater than 0' }
                                            })}
                                            onChange={handleRateChange}
                                            className="w-full border border-gray-300 rounded-xl px-4 py-2 shadow-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="Enter rate"
                                        />
                                        {errors.rate && <p className="text-red-500 text-xs mt-1">{errors.rate.message}</p>}
                                    </div>

                                    {/* Advance Payment */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Advance Payment</label>
                                        <input
                                            type="number"
                                            id="advancePayment"
                                            className="w-full border border-gray-300 rounded-xl px-4 py-2 shadow-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            {...register('advancePayment', { required: true })}
                                            placeholder="Enter advance payment"
                                            onChange={handleAdvancePaymentChange}
                                        />
                                        {errors.advancePayment && <span className="text-red-500 text-xs mt-1">This field is required</span>}
                                    </div>
                                </div>

                                {/* Total & Balance Amount */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                    <div className="bg-gray-100 shadow-md rounded-xl p-4">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Total Amount</label>
                                        <input
                                            type="text"
                                            value={totalAmount}
                                            readOnly
                                            className="w-full border border-gray-300 rounded-xl px-4 py-2 shadow-sm bg-gray-200 text-gray-700"
                                        />
                                    </div>

                                    <div className="bg-gray-100 shadow-md rounded-xl p-4">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Balance Amount</label>
                                        <input
                                            type="text"
                                            value={balancePayment}
                                            readOnly
                                            className="w-full border border-gray-300 rounded-xl px-4 py-2 shadow-sm bg-gray-200 text-gray-700"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Additional Details */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4 text-gray-700">Additional Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Driver Name</label>
                                        <input
                                            {...register('driverName')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter driver name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Driver Contact</label>
                                        <input
                                            {...register('driverContact')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter driver contact"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Name</label>
                                        <input
                                            {...register('referenceName')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter reference name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Contact</label>
                                        <input
                                            {...register('referenceContact')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter reference contact"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                                        <select
                                            {...register('paymentStatus')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="PENDING">Pending</option>
                                            <option value="PAID">Paid</option>
                                            <option value="PARTIAL">Partial</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                    <textarea
                                        {...register('remarks')}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows="2"
                                        placeholder="Enter any additional remarks"
                                    />
                                </div>

                            </div>

                            {/* Vehicle Information */}


                            <div className="flex justify-end gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseForm}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : editingReceipt ? 'Update Receipt' : 'Create Receipt'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {renderReceiptTable()}

            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                    {/* Previous Button */}
                    <button
                        onClick={() => currentPage > 1 && fetchReceipts(currentPage - 1, activeFilters)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded ${currentPage === 1
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                    >
                        &laquo;
                    </button>

                    {/* First Page - Always show if not in first few pages */}
                    {currentPage > 3 && totalPages > 5 && (
                        <>
                            <button
                                onClick={() => fetchReceipts(1, activeFilters)}
                                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                            >
                                1
                            </button>
                            {currentPage > 4 && <span className="px-1">...</span>}
                        </>
                    )}

                    {/* Dynamic Page Numbers */}
                    {(() => {
                        const pages = [];
                        let startPage = Math.max(1, currentPage - 2);
                        let endPage = Math.min(totalPages, currentPage + 2);

                        // Adjust if we're near the beginning
                        if (currentPage <= 3) {
                            endPage = Math.min(5, totalPages);
                        }

                        // Adjust if we're near the end
                        if (currentPage >= totalPages - 2) {
                            startPage = Math.max(1, totalPages - 4);
                        }

                        for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                                <button
                                    key={i}
                                    onClick={() => fetchReceipts(i, activeFilters)}
                                    className={`px-3 py-1 rounded ${currentPage === i
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 hover:bg-gray-300'
                                        }`}
                                >
                                    {i}
                                </button>
                            );
                        }
                        return pages;
                    })()}

                    {/* Last Page - Always show if not in last few pages */}
                    {currentPage < totalPages - 2 && totalPages > 5 && (
                        <>
                            {currentPage < totalPages - 3 && <span className="px-1">...</span>}
                            <button
                                onClick={() => fetchReceipts(totalPages, activeFilters)}
                                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                            >
                                {totalPages}
                            </button>
                        </>
                    )}

                    {/* Next Button */}
                    <button
                        onClick={() => currentPage < totalPages && fetchReceipts(currentPage + 1, activeFilters)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded ${currentPage === totalPages
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                    >
                        &raquo;
                    </button>
                </div>
            )}
        </div>
    );
};

export default TransportReceipt;
